import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const Navigation = ({ activeSection, onNavigate, nightMode }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [pillStyle, setPillStyle] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const itemRefs = useRef({});

  const navItems = [
    { id: "about", label: "About" },
    { id: "archive", label: "Education" },
    { id: "work", label: "Works" },
    { id: "askbot", label: "AskBot" },
    { id: "drop", label: "The Drop" },
    { id: "resume", label: "Résumé" },
    { id: "contact", label: "Connect" },
  ];

  // Determine which item should have the pill
  const currentItem = hoveredItem || activeSection || "work";

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      // Auto-expand on mobile
      if (window.innerWidth <= 768) {
        setIsExpanded(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isExpanded) {
      // Small delay to ensure DOM is ready after expansion
      setTimeout(() => {
        updatePillPosition(currentItem);
      }, 50);
    }
  }, [currentItem, isExpanded]);

  const updatePillPosition = (itemId) => {
    if (!itemId || !itemRefs.current[itemId]) return;

    const itemElement = itemRefs.current[itemId];
    const wrapperElement = itemElement.parentElement;
    const itemRect = itemElement.getBoundingClientRect();
    const wrapperRect = wrapperElement.getBoundingClientRect();

    setPillStyle({
      width: `${itemRect.width}px`,
      height: `${itemRect.height}px`,
      transform: `translateX(${itemRect.left - wrapperRect.left}px)`,
    });

    // Update active item in sync with pill position
    setActiveItem(itemId);
  };

  const handleMouseEnter = (itemId) => {
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleLogoClick = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNavClick = (item) => {
    // Close mobile menu when navigating
    setMobileMenuOpen(false);

    if (item.id === "archive") {
      navigate("/education");
    } else if (item.id === "work") {
      navigate("/works");
    } else if (item.id === "askbot") {
      navigate("/askbot");
    } else if (item.id === "about") {
      navigate("/");
      setTimeout(() => {
        const aboutSection = document.getElementById("about");
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else if (item.id === "contact") {
      navigate("/");
      setTimeout(() => {
        const contactSection = document.getElementById("contact");
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else if (item.id === "drop") {
      navigate("/drop");
    } else if (item.id === "resume") {
      navigate("/resume");
    } else {
      onNavigate(item.id);
    }
  };

  // Mobile Navigation
  if (isMobile) {
    return (
      <>
        <nav className="mobile-nav-bar">
          <button className="mobile-nav-logo-button" onClick={handleLogoClick}>
            <img
              src={nightMode ? "/logo-white.png" : "/logo.png"}
              alt="Logo"
              className="mobile-nav-logo"
            />
          </button>
          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-content">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`mobile-menu-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Desktop Navigation
  return (
    <nav
      className={`main-navigation ${isExpanded ? "expanded" : ""}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className="nav-container-glass" ref={navRef}>
        <button className="nav-logo-button" onClick={handleLogoClick}>
          <img
            src={nightMode ? "/logo-white.png" : "/logo.png"}
            alt="Logo"
            className="nav-logo"
          />
        </button>
        <div className="nav-items-wrapper">
          <div className="nav-pill-background" style={pillStyle} />
          {navItems.map((item) => (
            <button
              key={item.id}
              ref={(el) => (itemRefs.current[item.id] = el)}
              className={`nav-item-glass ${activeItem === item.id ? "active" : ""}`}
              onClick={() => handleNavClick(item)}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
